import React, { useEffect, useState } from "react";
import { Alert, Card, Form, Tab, Tabs } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
    add_beneficiary,
    get_beneficiary_status,
    get_districts_list,
    get_states,
} from "../../helper/Api";
import AddBeneficiaryPreview from "../Modal/AddBeneficiaryPreview";
import CustomBreadcrumbs from "../Tools/Breadcumbs";
import BasicInformationFields from "./components/BasicInformationFields";
import BankDetailsFields from "./components/BankDetailsFields";
import OtherDetailsFields from "./components/OtherDetailsFields";
import PartnerDetailsFields from "./components/PartnerDetailsFields";
import StatutoryDetailsFields from "./components/StatutoryDetailsFields";
import "./AddBeneficiaryForm.css";
import Spinner from "../Tools/spinner";

const createInitialFormData = (authData, beneficiaryType) => ({
    company_name: "",
    address_1: "",
    address_2: "",
    state_id: "",
    district: "",
    pin_code: "",
    contact_person: "",
    email: "",
    mobile: "",
    landline_no: "",
    beneficiary_name: "",
    bank_name: "",
    branch_name: "",
    account_no: "",
    ifsc_code: "",
    pan_no: "",
    pan_holder_name: "",
    status: "",
    gst_no: "",
    reg_no: "",
    added_by: authData?.user?.id || "",
    p_name: "",
    p_pan_no: "",
    p_dob: "",
    p_mobile: "",
    p_email: "",
    p_address_1: "",
    p_address_2: "",
    p_state_id: "",
    p_district: "",
    p_pincode: "",
    payment_terms: "",
    cost_code: "",
    project_name: "",
    department_id: authData?.user?.department_id || "",
    type: beneficiaryType,
    is_agreement_available: "",
    contract_copy: null,
    reg_cert: null,
    pan_card: null,
    authority_letter: null,
    letter_head: null,
    cancel_cheque: null,
});

const AddBeneficiaryForm = () => {
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const type = queryParams.get("type");
    const beneficiaryType = type === "agency" ? "agency" : "vendor";
    const title = beneficiaryType === "agency" ? "Agency" : "Beneficiary";
    const { authData } = useSelector((state) => state.authData);
    const navigate = useNavigate();

    const [statusList, setStatusList] = useState([]);
    const [statesList, setStatesList] = useState([]);
    const [beneficiaryDistrictList, setBeneficiaryDistrictList] = useState([]);
    const [partnerDistrictList, setPartnerDistrictList] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [activeTab, setActiveTab] = useState("BasicInformation");
    const [formData, setFormData] = useState(() => createInitialFormData(authData, beneficiaryType));
    const [error, setError] = useState({
        status: false,
        msg: "",
    });

    useEffect(() => {
        fetchStatusAndStates();
    }, []);

    useEffect(() => {
        setFormData((prevState) => ({
            ...prevState,
            type: beneficiaryType,
        }));
    }, [beneficiaryType]);

    useEffect(() => {
        if (!formData.p_state_id) {
            setPartnerDistrictList([]);
            return;
        }

        fetchPartnerDistricts();
    }, [formData.p_state_id]);

    useEffect(() => {
        if (!formData.state_id) {
            setBeneficiaryDistrictList([]);
            return;
        }

        fetchBeneficiaryDistricts();
    }, [formData.state_id]);

    const showErrorToast = (message) => {
        toast.error(message, {
            position: toast.POSITION.TOP_CENTER,
        });
    };

    const fetchStatusAndStates = async () => {
        setLoading(true);
        const [statusResult, statesResult] = await Promise.allSettled([
            get_beneficiary_status(),
            get_states(),
        ]);

        if (statusResult.status === "fulfilled" && statusResult.value.data.status) {
            setStatusList(statusResult.value.data.list || []);
        } else {
            setStatusList([]);
        }

        if (statesResult.status === "fulfilled" && statesResult.value.data.status) {
            setStatesList(statesResult.value.data.list || []);
        } else {
            setStatesList([]);
        } 
        setLoading(false);
    };

    const fetchPartnerDistricts = async () => {
        try {
            setLoading(true);
            const { data } = await get_districts_list(formData.p_state_id);
            if (data.status) {
                setPartnerDistrictList(data.list);
            } else {
                setPartnerDistrictList([]);
            }
        } catch (fetchError) {
            setPartnerDistrictList([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchBeneficiaryDistricts = async () => {
        try {
            setLoading(true);
            const { data } = await get_districts_list(formData.state_id);
            if (data.status) {
                setBeneficiaryDistrictList(data.list);
            } else {
                setBeneficiaryDistrictList([]);
            }
        } catch (fetchError) {
            setBeneficiaryDistrictList([]);
        } finally {
            setLoading(false);
        }

    };

    const handleFormDataChange = (event, isFile = false) => {
        const { name } = event.target;
        let value = isFile ? event.target.files : event.target.value;

        if (!isFile) {
            if (["pin_code", "mobile", "landline_no", "p_mobile", "p_pincode", "account_no"].includes(name)) {
                value = value.replace(/\D/g, "");
            }

            if (["pin_code", "p_pincode"].includes(name)) {
                value = value.slice(0, 6);
            }

            if (["mobile", "p_mobile"].includes(name)) {
                value = value.slice(0, 10);
            }

            if (name === "landline_no") {
                value = value.slice(0, 15);
            }

            if (name === "account_no") {
                value = value.slice(0, 30);
            }

            if (["pan_no", "p_pan_no"].includes(name)) {
                value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
            }

            if (name === "ifsc_code") {
                value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
            }

            if (name === "gst_no") {
                value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15);
            }

            if (name === "reg_no") {
                value = value.toUpperCase().slice(0, 19);
            }
        }

        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
            ...(name === "state_id" ? { district: "" } : {}),
            ...(name === "p_state_id" ? { p_district: "" } : {}),
        }));
    };

    const hasValue = (value) => {
        if (value === null || value === undefined) {
            return false;
        }

        if (typeof value === "string") {
            return value.trim() !== "";
        }

        return true;
    };

    const validateForm = () => {
        const requiredFields = [
            { name: "company_name", label: `${title} Name`, tab: "BasicInformation" },
            { name: "address_1", label: "Address Line 1", tab: "BasicInformation" },
            { name: "state_id", label: "State", tab: "BasicInformation" },
            { name: "district", label: "District", tab: "BasicInformation" },
            { name: "pin_code", label: "PIN Code", tab: "BasicInformation" },
            { name: "contact_person", label: "Contact Person", tab: "BasicInformation" },
            { name: "email", label: "Email ID", tab: "BasicInformation" },
            { name: "mobile", label: "Mobile No", tab: "BasicInformation" },
            { name: "project_name", label: "Project Name", tab: "BankDetails" },
            { name: "beneficiary_name", label: "Beneficiary Name", tab: "BankDetails" },
            { name: "bank_name", label: "Bank Name", tab: "BankDetails" },
            { name: "branch_name", label: "Branch Name", tab: "BankDetails" },
            { name: "account_no", label: "Account No", tab: "BankDetails" },
            { name: "ifsc_code", label: "IFSC Code", tab: "BankDetails" },
            { name: "pan_no", label: "PAN", tab: "TaxDetails" },
            { name: "pan_holder_name", label: "PAN Holder Name", tab: "TaxDetails" },
            { name: "status", label: "Status", tab: "TaxDetails" },
            { name: "is_agreement_available", label: "Agreement Availability", tab: "OtherDetails" },
        ];

        for (const field of requiredFields) {
            if (!hasValue(formData[field.name])) {
                setActiveTab(field.tab);
                showErrorToast(`Please enter ${field.label}.`);
                return false;
            }
        }

        if (!/^\d{6}$/.test(formData.pin_code)) {
            setActiveTab("BasicInformation");
            showErrorToast("PIN Code must be 6 digits.");
            return false;
        }

        if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
            setActiveTab("BasicInformation");
            showErrorToast("Mobile No must start with 6, 7, 8, or 9 and be 10 digits.");
            return false;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            setActiveTab("BasicInformation");
            showErrorToast("Please enter a valid Email ID.");
            return false;
        }

        if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.pan_no)) {
            setActiveTab("TaxDetails");
            showErrorToast("Please enter a valid PAN (e.g., ABCDE1234F).");
            return false;
        }

        const gstOptionalStatuses = new Set([
            "individual",
            "proprietor",
            "state govt department",
            "centre govt department",
        ]);
        const selectedStatusTitle = (
            statusList.find((status) => String(status.id) === String(formData.status))?.title || ""
        ).trim().toLowerCase();
        const isGstOptional = gstOptionalStatuses.has(selectedStatusTitle);

        if (!isGstOptional && !hasValue(formData.gst_no)) {
            setActiveTab("TaxDetails");
            showErrorToast("GST No is required for selected status.");
            return false;
        }

        if (hasValue(formData.gst_no) && !/^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(formData.gst_no)) {
            setActiveTab("TaxDetails");
            showErrorToast("Please enter a valid GST No (e.g., 22ABCDE1234F1Z5).");
            return false;
        }

        if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc_code)) {
            setActiveTab("BankDetails");
            showErrorToast("Please enter a valid IFSC Code (e.g., SBIN0001234).");
            return false;
        }

        if (formData.p_mobile && !/^[6-9]\d{9}$/.test(formData.p_mobile)) {
            setActiveTab("Proprietor");
            showErrorToast("Partner phone number must start with 6, 7, 8, or 9 and be 10 digits.");
            return false;
        }

        if (formData.p_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.p_email)) {
            setActiveTab("Proprietor");
            showErrorToast("Please enter a valid partner email address.");
            return false;
        }

        if (formData.p_pincode && !/^\d{6}$/.test(formData.p_pincode)) {
            setActiveTab("Proprietor");
            showErrorToast("Partner PIN Code must be 6 digits.");
            return false;
        }

        if (formData.is_agreement_available === "1") {
            if (!formData.contract_copy?.[0]) {
                setActiveTab("OtherDetails");
                showErrorToast("Contract Copy is required when agreement is available.");
                return false;
            }
            if (!formData.reg_cert?.[0]) {
                setActiveTab("OtherDetails");
                showErrorToast("Firm Registration Certificate is required when agreement is available.");
                return false;
            }
        }

        const supportingDocuments = ["authority_letter", "cancel_cheque", "pan_card"];
        const hasUploadedDocument = supportingDocuments.some((field) => formData[field]?.[0]);

        if (!hasUploadedDocument) {
            setActiveTab("OtherDetails");
            showErrorToast("Please upload at least one document: Authority Letter, Cancel Cheque, or PAN Card.");
            return false;
        }

        return true;
    };

    const handleSubmit = async (event) => {
        if (event && typeof event.preventDefault === "function") {
            event.preventDefault();
        }
        setLoading(true);

        if (!validateForm()) {
            return;
        }

        const postData = new FormData();
        const fieldsToAppend = [
            "company_name",
            "address_1",
            "address_2",
            "state_id",
            "district",
            "pin_code",
            "contact_person",
            "email",
            "mobile",
            "landline_no",
            "beneficiary_name",
            "bank_name",
            "branch_name",
            "account_no",
            "ifsc_code",
            "pan_no",
            "pan_holder_name",
            "status",
            "gst_no",
            "reg_no",
            "added_by",
            "p_name",
            "p_pan_no",
            "p_dob",
            "p_mobile",
            "p_email",
            "p_address_1",
            "p_address_2",
            "p_state_id",
            "p_district",
            "p_pincode",
            "payment_terms",
            "cost_code",
            "project_name",
            "department_id",
            "type",
            "is_agreement_available",
        ];

        fieldsToAppend.forEach((field) => {
            postData.append(field, formData[field] ?? "");
        });

        ["contract_copy", "reg_cert", "pan_card", "authority_letter", "letter_head", "cancel_cheque"].forEach((field) => {
            const file = formData[field]?.[0];
            if (file) {
                postData.append(field, file);
            }
        });

        try {
            const response = await add_beneficiary(postData);
            const responseData = response.data;

            if (responseData.status) {
                setError({
                    status: false,
                    msg: responseData?.message || responseData?.error || "Something went wrong",
                });
                setShowPreview(false);
                navigate("/beneficiary");
            } else {
                showErrorToast(responseData?.message || responseData?.error || "Something went wrong");
            }
        } catch (submitError) {
            showErrorToast("Something went wrong...");
            console.error(submitError);
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = (event) => {
        event.preventDefault();

        if (!validateForm()) {
            return;
        }

        setShowPreview(true);
    };

    return (
        <div className="p-3">
            {loading && <div > <Spinner/> </div>}
            <div className="row" style={{ padding: "10px 16px" }}>
                <div className="col-md-6">
                    <h4 style={{ color: "#28326f", margin: 0 }}>{title}</h4>
                    <CustomBreadcrumbs linksArray={[]} title={title} />
                </div>
                <div className="col-md-6">
                    <div className="d-flex gap-2" style={{ width: "auto", float: "right" }}>
                        <button type="button" className="submit_button" onClick={handlePreview}>
                            <span className="fs-14px">Save Beneficiary</span>
                        </button>
                    </div>
                </div>
            </div>
            <AddBeneficiaryPreview
                showPreview={showPreview}
                setShowPreview={setShowPreview}
                formData={formData}
                handleSubmit={handleSubmit}
                title={title}
                allStates={statesList}
                beneficiaryDistricts={beneficiaryDistrictList}
                partnerDistricts={partnerDistrictList}
                allStatus={statusList}
            />
            {error.status && (
                <Alert
                    variant="danger"
                    onClose={() => setError({ status: false, msg: "" })}
                    dismissible
                >
                    {error.msg}
                </Alert>
            )}
            <Form onSubmit={handlePreview}>
                <Card className="mt-2">
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(key) => setActiveTab(key || "BasicInformation")}
                        id="beneficiary-form-tabs"
                        className="mb-3"
                    >
                        <Tab eventKey="BasicInformation" title="Basic Information" className="p-2">
                            <BasicInformationFields
                                title={title}
                                formData={formData}
                                handleFormDataChange={handleFormDataChange}
                                statesList={statesList}
                                districtList={beneficiaryDistrictList}
                            />
                        </Tab>
                        <Tab eventKey="BankDetails" title="Bank Details" className="p-2">
                            <BankDetailsFields formData={formData} handleFormDataChange={handleFormDataChange} />
                        </Tab>
                        <Tab eventKey="TaxDetails" title="Statutory Details" className="p-2">
                            <StatutoryDetailsFields
                                formData={formData}
                                handleFormDataChange={handleFormDataChange}
                                statusList={statusList}
                            />
                        </Tab>
                        <Tab eventKey="OtherDetails" title="Other Details" className="p-2">
                            <OtherDetailsFields formData={formData} handleFormDataChange={handleFormDataChange} />
                        </Tab>
                        <Tab
                            eventKey="Proprietor"
                            title="Proprietor/Partner/Director's PAN Details"
                            className="p-2"
                        >
                            <PartnerDetailsFields
                                formData={formData}
                                handleFormDataChange={handleFormDataChange}
                                statesList={statesList}
                                districtList={partnerDistrictList}
                            />
                        </Tab>
                    </Tabs>
                </Card>
            </Form>
        </div>
    );
};

export default AddBeneficiaryForm;